/**
 * pptxBytesShared.js — single source of truth for the active deck's bytes.
 *
 * Why this module exists
 * ----------------------
 * `Office.context.document.getFileAsync` is the only way to read the live
 * presentation bytes from inside a task pane. The API has two known sharp
 * edges that bite when more than one part of the codebase reads the file:
 *
 *   1. CONCURRENCY: calling `getFileAsync` while a previous one is still
 *      in flight (or its slices haven't been fully read) intermittently
 *      returns "An internal error has occurred." for both calls. The
 *      symptom is two independent retry loops (e.g. SlideExtractor and
 *      BrandMerger) blowing up at the same time on panel open.
 *
 *   2. POST-WRITE STATE: right after Office.js mutates the deck (insert/
 *      delete slide, brand application, etc.) the next `getFileAsync`
 *      can fail until PowerPoint flushes. A short retry usually clears it.
 *
 * Both problems go away when EVERY caller goes through one cached, in-
 * flight-aware fetcher with a single retry policy. That's this module.
 *
 * Behaviour
 * ---------
 *  - First call → fires `getFileAsync`, reads slices, caches the bytes
 *    for `TTL_MS`, returns the buffer.
 *  - Concurrent callers while a fetch is in flight → all join the same
 *    promise (no second `getFileAsync` is ever issued).
 *  - Calls within `TTL_MS` after a successful fetch → cache hit, no API
 *    call.
 *  - "Internal error" responses are retried up to `MAX_RETRIES` times
 *    with exponential backoff.
 *
 * Cache invalidation
 * ------------------
 * Callers that mutate the deck (slide insert, brand apply, etc.) should
 * call `clearSharedPptxCache()` so the next read pulls fresh bytes.
 */

const TTL_MS = 3000;
const MAX_RETRIES = 3;
const SLICE_SIZE = 4 * 1024 * 1024; // 4 MB — matches what SlideExtractor used to use
// Read this many slices concurrently. Reading slices one-at-a-time made
// large, image-heavy decks (50+ slides) slow enough to blow the fetch
// timeout; a small bounded pool speeds the read up substantially without
// flooding Office with getSliceAsync calls.
const MAX_CONCURRENT_SLICES = 4;
// Hard ceiling on a single fetch attempt. If Office's getFileAsync /
// getSliceAsync callback never fires (rare Office.js bug, more common
// when the host is busy), we fail fast instead of leaving every caller
// joined onto a dead promise — that's what froze the taskpane. Set
// generously so a genuinely large deck that's just slow to read isn't
// killed mid-fetch (parallel slice reads keep the common case well under).
const FETCH_TIMEOUT_MS = 60000;

let _cache: ArrayBuffer | null = null;
let _cacheAt = 0;        // ms timestamp
let _pending: Promise<ArrayBuffer> | null = null;

/**
 * Get the current presentation as an ArrayBuffer.
 * Cached; concurrent calls share one in-flight fetch.
 *
 * @param {boolean} [forceRefresh=false] — bypass the TTL cache (still
 *   joins any in-flight fetch).
 * @returns {Promise<ArrayBuffer>}
 */
export function getActivePresentationBytesShared(forceRefresh = false) {
  const now = Date.now();
  if (!forceRefresh && _cache && (now - _cacheAt) < TTL_MS) {
    return Promise.resolve(_cache);
  }
  if (_pending) return _pending;

  _pending = _withTimeout(
    _fetchWithRetry(),
    FETCH_TIMEOUT_MS,
    "getActivePresentationBytes",
  )
    .then((buf) => {
      _cache = buf;
      _cacheAt = Date.now();
      return buf;
    })
    .finally(() => {
      _pending = null;
    });

  return _pending;
}

/**
 * Drop the cached bytes. Call after any operation that mutates the active
 * deck (slide insert/delete, brand apply, etc.) so the next read fetches
 * fresh data.
 */
export function clearSharedPptxCache() {
  _cache = null;
  _cacheAt = 0;
  // Note: we deliberately don't cancel an in-flight fetch — let it
  // resolve its callers. The next fetch after it lands will be fresh.
}

/**
 * Marker error for a getFileAsync "Failed" result, so the retry loop can tell a
 * (retryable) transient host error apart from a context/exception/slice failure.
 */
class FileFetchError extends Error {
  /**
   * Office's numeric `AsyncResult.error.code`, captured for diagnostics. Office.js
   * doesn't publish a stable code for the transient "internal error", so
   * {@link _fetchWithRetry} still decides retryability from the message; the code
   * rides along for logging/telemetry.
   */
  constructor(
    message: string,
    readonly code?: number,
  ) {
    super(message);
  }
}

/** Resolve after `ms` — backoff between retries, with no nested callback. */
function _sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * A single getFileAsync attempt, drained to one ArrayBuffer. Rejects with a
 * {@link FileFetchError} on a Failed result (retryable), or a plain Error for a
 * missing Office context / thrown call / slice failure (not retryable).
 */
function _fetchOnce(): Promise<ArrayBuffer> {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    if (typeof Office === "undefined" || !Office?.context?.document) {
      reject(new Error("Office context not available — is the add-in still attached?"));
      return;
    }
    try {
      Office.context.document.getFileAsync(
        Office.FileType.Compressed,
        { sliceSize: SLICE_SIZE },
        (result: Office.AsyncResult<Office.File>) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            reject(new FileFetchError(result.error?.message || "unknown", result.error?.code));
            return;
          }
          // A Succeeded status should always carry a file, but guard against a
          // null value so a host quirk fails with a clear message instead of a
          // cryptic "cannot read sliceCount of undefined" inside _drainSlices.
          if (!result.value) {
            reject(new Error("getFileAsync succeeded but returned no file."));
            return;
          }
          void _drainSlices(result.value).then(resolve).catch(reject);
        },
      );
    } catch (err) {
      reject(new Error(`getFileAsync threw: ${err instanceof Error ? err.message : String(err)}`));
    }
  });
}

/**
 * One-shot fetch with retry on transient "internal error" responses. Flattened
 * to async/await so no callback nests deeper than the getFileAsync handler.
 *
 * @returns {Promise<ArrayBuffer>}
 */
async function _fetchWithRetry(): Promise<ArrayBuffer> {
  const maxAttempts = MAX_RETRIES + 1; // one initial try + MAX_RETRIES retries
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await _fetchOnce();
    } catch (err) {
      // Context / exception / slice failures are not retryable — propagate.
      if (!(err instanceof FileFetchError)) throw err;

      const msg = err.message;
      // Office.js exposes no stable code for the transient "internal error",
      // so retryability is decided from the message; err.code is logged for
      // diagnostics only.
      const transient = msg.toLowerCase().includes("internal");
      if (!transient || attempt === maxAttempts) {
        throw new Error(
          `Could not read the presentation file: ${msg}. ` +
          `Try saving the deck (Ctrl+S) and clicking again.`,
        );
      }

      const backoff = attempt * 400;
      console.warn(
        `[pptxBytesShared] getFileAsync failed (code=${err.code ?? "?"}, "${msg}"), ` +
        `retrying in ${backoff}ms… (attempt ${attempt} of ${maxAttempts})`,
      );
      await _sleep(backoff);
    }
  }
  // Unreachable — the loop always returns on success or throws on failure.
  throw new Error("Could not read the presentation file.");
}

/**
 * Read every slice of an Office file handle and concatenate into one
 * ArrayBuffer. Closes the handle when done.
 *
 * @param {OfficeFile} file
 * @returns {Promise<ArrayBuffer>}
 */
/** Concatenate the received slices into one contiguous ArrayBuffer. */
function _combineSlices(slices: Uint8Array[]): ArrayBuffer {
  const total = slices.reduce((sum, sl) => sum + sl.byteLength, 0);
  const combined = new Uint8Array(total);
  let off = 0;
  for (const sl of slices) {
    combined.set(sl, off);
    off += sl.byteLength;
  }
  return combined.buffer;
}

/**
 * Close an Office file handle, logging (but not failing on) a close error, then
 * run `after`. A close rarely fails, and by then we already have — or have
 * given up on — the bytes, so a failure is diagnostic-only.
 */
function _closeFile(file: Office.File, after: () => void): void {
  file.closeAsync((res) => {
    if (res?.status === Office.AsyncResultStatus.Failed) {
      console.warn(`[pptxBytesShared] closeAsync failed: ${res.error?.message || "?"}`);
    }
    after();
  });
}

function _drainSlices(file: Office.File): Promise<ArrayBuffer> {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const sliceCount = file.sliceCount;
    if (sliceCount === 0) {
      _closeFile(file, () =>
        reject(new Error("Presentation appears empty. Save the file (Ctrl+S) and try again.")),
      );
      return;
    }

    const slices: Uint8Array[] = new Array(sliceCount);
    let received = 0;
    let nextIdx = 0;
    let failed = false;
    // Holder so fail/finish (defined before the timer) can clear it once set.
    const stall: { timer?: ReturnType<typeof setTimeout> } = {};

    const fail = (msg: string) => {
      if (failed) return;
      failed = true;
      clearTimeout(stall.timer);
      _closeFile(file, () => reject(new Error(msg)));
    };

    const finish = () => {
      clearTimeout(stall.timer);
      _closeFile(file, () => resolve(_combineSlices(slices)));
    };

    // Each "lane" reads a slice, then pulls the next un-started index. Up to
    // MAX_CONCURRENT_SLICES lanes run at once, so reads overlap instead of
    // waiting one-at-a-time — much faster on large, image-heavy decks.
    const readNext = () => {
      if (failed || nextIdx >= sliceCount) return;
      const idx = nextIdx++;
      file.getSliceAsync(idx, (sr: Office.AsyncResult<Office.Slice>) => {
        if (failed) return;
        if (sr.status === Office.AsyncResultStatus.Failed) {
          fail(`Slice read failed: ${sr.error?.message || "?"}`);
          return;
        }
        // getSliceAsync returns the slice as a byte array; the Uint8Array
        // constructor also accepts a typed array, so one path covers both.
        // A missing value/data would otherwise make an empty slice and a
        // silently short buffer, so fail loudly instead.
        const data = sr.value?.data;
        if (!data) {
          fail(`Slice ${idx} returned no data.`);
          return;
        }
        slices[idx] = new Uint8Array(data);
        received++;
        if (received === sliceCount) {
          finish();
        } else {
          readNext();
        }
      });
    };

    // Safety net: if Office stops firing slice/close callbacks entirely, neither
    // finish nor fail would run and the file handle would leak — the outer
    // _withTimeout only rejects the caller, it can't close this handle. On a
    // stall, close the handle and fail so the resource is always released.
    stall.timer = setTimeout(
      () => fail("Reading the presentation stalled — no response from PowerPoint."),
      FETCH_TIMEOUT_MS,
    );

    const lanes = Math.min(MAX_CONCURRENT_SLICES, sliceCount);
    for (let i = 0; i < lanes; i++) readNext();
  });
}

/**
 * Wrap a promise with a hard timeout. Used to guarantee the shared
 * fetcher fails fast if Office's getFileAsync / getSliceAsync callbacks
 * never fire — without this, every caller awaiting bytes would freeze
 * indefinitely.
 *
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @param {string} label
 * @returns {Promise<T>}
 */
function _withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      console.error(`[pptxBytesShared] ${label} timed out after ${ms}ms — aborting wait.`);
      reject(new Error(
        `${label} timed out after ${ms / 1000}s. ` +
        `PowerPoint may be busy; try saving (Ctrl+S) and reopening the add-in.`,
      ));
    }, ms);
    promise.then(
      (val) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(val);
      },
      (err: unknown) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

// Console-accessible escape hatch. If the taskpane gets stuck in a state
// where every read is joining a dead promise (HMR mishap, Office.js bug,
// etc.), call `__resetPptxCache()` from DevTools to drop ALL cached state
// and let the next read start fresh.
if (typeof window !== "undefined") {
  (window as unknown as { __resetPptxCache?: () => void }).__resetPptxCache = () => {
    console.warn("[pptxBytesShared] manual cache reset — dropping bytes and any in-flight promise reference.");
    _cache = null;
    _cacheAt = 0;
    // Note: doesn't cancel the underlying API call, just stops new callers
    // from joining a stuck promise.
    _pending = null;
  };
}
